import { collection, doc, onSnapshot, orderBy, query, updateDoc, Timestamp } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '../../firebase'
import { 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import { 
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material'

interface ComplaintDoc {
  id: string
  userId?: string
  userName?: string
  userPhone?: string
  userType?: string
  type?: string
  subject?: string
  message?: string
  status?: string
  createdAt?: any
  updatedAt?: any
}

type ComplaintStatus = 'pending' | 'resolved' | 'all'

const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  'pending': { label: 'قيد المراجعة', color: 'warning' },
  'resolved': { label: 'تم الحل', color: 'success' }
}

export default function ComplaintsPage() {
  const [rows, setRows] = useState<ComplaintDoc[]>([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<ComplaintStatus>('all')
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; complaint?: ComplaintDoc }>({ open: false })
  const [resolveDialog, setResolveDialog] = useState<{ open: boolean; complaintId?: string }>({ open: false })
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' })
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const q = query(collection(db, 'complaints'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(q, (snapshot) => {
      const complaints: ComplaintDoc[] = snapshot.docs.map(d => ({ 
        id: d.id, 
        ...(d.data() as any)
      }))
      setRows(complaints)
    })

    return () => unsub()
  }, [])

  const filteredRows = rows.filter(row => {
    const matchesStatus = statusFilter === 'all' || row.status === statusFilter
    const matchesSearch = !searchQuery || 
      row.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.userPhone?.includes(searchQuery)
    return matchesStatus && matchesSearch
  })

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filteredRows.length / pageSize)

  const handleViewDetails = (complaint: ComplaintDoc) => {
    setDetailsDialog({ open: true, complaint })
  }

  const handleResolve = async (complaintId: string) => {
    try {
      await updateDoc(doc(db, 'complaints', complaintId), {
        status: 'resolved',
        updatedAt: Timestamp.now()
      })
      setSnackbar({ open: true, message: 'تم تحديث حالة الشكوى بنجاح', severity: 'success' })
      setResolveDialog({ open: false })
    } catch (error) {
      console.error('خطأ في تحديث حالة الشكوى:', error)
      setSnackbar({ open: true, message: 'حدث خطأ في تحديث حالة الشكوى', severity: 'error' })
    }
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'غير محدد'
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'غير محدد'
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          إدارة الشكاوى
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="بحث في الشكاوى..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>حالة الشكوى</InputLabel>
                <Select
                  value={statusFilter}
                  label="حالة الشكوى"
                  onChange={(e) => {
                    setStatusFilter(e.target.value as ComplaintStatus)
                    setPage(1)
                  }}
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="pending">قيد المراجعة</MenuItem>
                  <MenuItem value="resolved">تم الحل</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip
                icon={<FilterIcon />}
                label={`إجمالي: ${filteredRows.length}`}
                color="primary"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>التاريخ</strong></TableCell>
              <TableCell><strong>المستخدم</strong></TableCell>
              <TableCell><strong>نوع الشكوى</strong></TableCell>
              <TableCell><strong>العنوان</strong></TableCell>
              <TableCell><strong>الحالة</strong></TableCell>
              <TableCell><strong>الإجراءات</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    لا توجد شكاوى
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {row.userName || 'غير محدد'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.userPhone || 'غير متوفر'}
                      </Typography>
                      {row.userType && (
                        <Chip
                          label={row.userType === 'buyer' ? 'مشتري' : row.userType === 'seller' ? 'تاجر' : 'أدمن'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.type || 'عام'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {row.subject || 'بدون عنوان'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.status && statusMap[row.status] ? (
                      <Chip
                        label={statusMap[row.status].label}
                        color={statusMap[row.status].color}
                        size="small"
                      />
                    ) : (
                      <Chip label="غير محدد" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="عرض التفاصيل">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(row)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {row.status === 'pending' && (
                        <Tooltip title="تم الحل">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => setResolveDialog({ open: true, complaintId: row.id })}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* تفاصيل الشكوى */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">تفاصيل الشكوى</Typography>
            <IconButton onClick={() => setDetailsDialog({ open: false })}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailsDialog.complaint && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    المستخدم
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {detailsDialog.complaint.userName || 'غير محدد'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    رقم الهاتف
                  </Typography>
                  <Typography variant="body1">
                    {detailsDialog.complaint.userPhone || 'غير متوفر'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    نوع المستخدم
                  </Typography>
                  <Typography variant="body1">
                    {detailsDialog.complaint.userType === 'buyer' ? 'مشتري' : 
                     detailsDialog.complaint.userType === 'seller' ? 'تاجر' : 
                     detailsDialog.complaint.userType === 'admin' ? 'أدمن' : 'غير محدد'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    نوع الشكوى
                  </Typography>
                  <Typography variant="body1">
                    {detailsDialog.complaint.type || 'عام'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    الحالة
                  </Typography>
                  <Box>
                    {detailsDialog.complaint.status && statusMap[detailsDialog.complaint.status] ? (
                      <Chip
                        label={statusMap[detailsDialog.complaint.status].label}
                        color={statusMap[detailsDialog.complaint.status].color}
                        size="small"
                      />
                    ) : (
                      <Chip label="غير محدد" size="small" />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    التاريخ
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(detailsDialog.complaint.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box>
                <Typography variant="caption" color="text.secondary">
                  عنوان الشكوى
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                  {detailsDialog.complaint.subject || 'بدون عنوان'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary">
                  تفاصيل الشكوى
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 1,
                    bgcolor: 'grey.50',
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailsDialog.complaint.message || 'لا توجد تفاصيل'}
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detailsDialog.complaint?.status === 'pending' && (
            <Button
              startIcon={<CheckCircleIcon />}
              variant="contained"
              color="success"
              onClick={() => {
                if (detailsDialog.complaint) {
                  handleResolve(detailsDialog.complaint.id)
                  setDetailsDialog({ open: false })
                }
              }}
            >
              تم الحل
            </Button>
          )}
          <Button onClick={() => setDetailsDialog({ open: false })}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* تأكيد الحل */}
      <Dialog
        open={resolveDialog.open}
        onClose={() => setResolveDialog({ open: false })}
      >
        <DialogTitle>تأكيد الحل</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من أنك تريد وضع علامة على هذه الشكوى كـ "تم الحل"؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false })}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (resolveDialog.complaintId) {
                handleResolve(resolveDialog.complaintId)
              }
            }}
          >
            تأكيد
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

